import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const iconMapping = {
  FaPhoneAlt: FaPhoneAlt,
  FaEnvelope: FaEnvelope,
};

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

function Hero({ heroImage }) {
  const { t } = useTranslation();
  const heroData = useMemo(
    () => t("components.contactUs.hero", { returnObjects: true }),
    [t]
  );

  return (
    <div
      className="relative h-[85vh] landscape:max-lg:h-[125vh] w-full overflow-hidden"
      id="safePad"
    >
      <motion.div
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 overflow-hidden"
      >
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center brightness-[0.6]"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-linear-to-br from-black via-gray-900/80 to-blue-900 opacity-95" />
      </motion.div>

      <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center justify-center">
        <div className="text-center space-y-8">
          <motion.h1
            variants={fadeInUpVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-tight"
            dangerouslySetInnerHTML={{ __html: heroData.heading }}
          />

          <motion.p
            variants={fadeInUpVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-md sm:text-lg md:text-xl lg:text-2xl text-gray-400 max-w-2xl mx-auto leading-relaxed"
          >
            {heroData.description}
          </motion.p>

          <motion.div
            variants={fadeInUpVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-wrap justify-center gap-6 mt-8"
          >
            {heroData.buttons.map((item, index) => {
              const IconComponent = iconMapping[item.icon];
              return (
                <motion.a
                  key={index}
                  href={item.href}
                  variants={fadeInUpVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="group flex items-center gap-4 bg-gray-800/80 backdrop-blur px-6 py-4 rounded-xl hover:bg-gray-700/80 transition-all duration-300"
                  aria-label={`${item.label}: ${item.value}`}
                >
                  <div className="p-3 rounded-lg bg-blue-600/20 text-blue-400 group-hover:bg-blue-600/30 transition-all duration-300">
                    {IconComponent && (
                      <IconComponent className="w-6 h-6" aria-hidden="true" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="text-sm text-gray-500">{item.label}</div>
                    <div className="text-sm lg:text-md text-white font-medium">
                      {item.value}
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-24 bg-linear-to-t from-black to-transparent" />
    </div>
  );
}

Hero.propTypes = {
  heroImage: PropTypes.string.isRequired,
};

export default React.memo(Hero);
